import { useEffect, useState } from "react"
import { SpinnerText } from "../components/ui/Spinner"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import AppLayout from "../layout/AppLayout"
import PatentProgressBar from "../components/patent/PatentProgressBar"
import PatentFeesSection from "../components/patent/PatentFeesSection"
import ServiceDescriptions from "../components/ui/ServiceDescriptions"
import {
  getPatentApplicationById,
  testRealEpoImport
} from "../services/patentApplicationService"
import {
  checkEuipoPaymentStatus,
  confirmStripeCheckoutSession,
  createStripeCheckoutSession,
  markCustomerPaymentReceived,
  submitInvoicePayment
} from "../services/stripePaymentService"
import { formatApiError } from "../utils/formatApiError"
import { getPatentApplicationNumber } from "../utils/patentApplicationNumber"
import { RootState } from "../store/store"

const ORIGIN_OF_RIGHT_LABELS: Record<string, string> = {
  EMPLOYER: "as employer",
  LEGAL_SUCCESSOR: "as legal successor",
  AGREEMENT: "by agreement"
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  DESCRIPTION: "Description",
  CLAIMS: "Claims",
  ABSTRACT: "Abstract",
  DRAWINGS: "Drawings"
}

export default function PatentPaymentPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = useSelector((state: RootState) => Boolean(state.auth.user?.isAdmin))
  const paymentResult = new URLSearchParams(location.search).get("payment")
  const stripeSessionId = new URLSearchParams(location.search).get("session_id")

  const [loading, setLoading] = useState(true)
  const [application, setApplication] = useState<any | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false)
  const [showInvoiceSuccess, setShowInvoiceSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checkingStatus, setCheckingStatus] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [epoTestResult, setEpoTestResult] = useState<any | null>(null)
  const [epoTestLoading, setEpoTestLoading] = useState(false)
  const [epoTestError, setEpoTestError] = useState<string | null>(null)

  async function loadApplication(applicationId: string) {
    const app = await getPatentApplicationById(applicationId)

    const canViewPaymentPage =
      app.status === "VALIDATED" ||
      app.status === "PAYMENT_PENDING" ||
      app.status === "AWAITING_RELEASE" ||
      app.status === "PAID" ||
      app.status === "ERROR" ||
      paymentResult === "success" ||
      isAdmin

    if (!canViewPaymentPage) {
      navigate(`/patent-applications/${applicationId}/edit`)
      return
    }

    setApplication(app)
  }

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }

    loadApplication(id).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, paymentResult, isAdmin])

  useEffect(() => {
    if (paymentResult !== "success" || !stripeSessionId) return

    async function confirmPayment(sessionId: string) {
      try {
        await confirmStripeCheckoutSession(sessionId)
        setShowPaymentSuccess(true)
      } catch (confirmError) {
        setError(
          formatApiError(
            confirmError,
            "The payment was completed, but the application status could not be updated. Please reload the page and try again."
          )
        )
      }
    }

    confirmPayment(stripeSessionId)
  }, [paymentResult, stripeSessionId])

  const applicationStatus = String(application?.status || "")
  const patentApplicationNumber = getPatentApplicationNumber(application)
  const patentFilingIdentifier = patentApplicationNumber
    ? {
        label: "EPO application number",
        value: patentApplicationNumber
      }
    : application?.epoSubmissionId
      ? {
          label: "EPO submission ID",
          value: application.epoSubmissionId
        }
      : null
  const paymentIsPaid = applicationStatus === "PAID"
  const userPaymentIsLocked =
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE" ||
    applicationStatus === "PAID" ||
    applicationStatus === "ERROR"
  const paymentReady = confirmed && (!userPaymentIsLocked || isAdmin)

  async function handleSubmit() {
    if (!id || submitting) return
    if (userPaymentIsLocked && !isAdmin) return

    setSubmitting(true)
    setError(null)

    try {
      const response = await createStripeCheckoutSession({
        applicationType: "patent",
        applicationId: id
      })

      window.location.href = response.url
    } catch (submitError) {
      setError(
        formatApiError(
          submitError,
          "The payment could not be started. Please reload the page and try again."
        )
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleInvoiceSubmit() {
    if (!id || submitting) return
    if (userPaymentIsLocked && !isAdmin) return

    setSubmitting(true)
    setError(null)

    try {
      await submitInvoicePayment({
        applicationType: "patent",
        applicationId: id
      })
      setShowConfirm(false)
      setShowInvoiceSuccess(true)
    } catch (submitError) {
      setError(
        formatApiError(
          submitError,
          "The invoice submission could not be started. Please reload the page and try again."
        )
      )
    } finally {
      setSubmitting(false)
    }
  }

  // SCRUM-54: once an application has been submitted (bank transfer
  // pending, or a failed attempt), the customer/admin needs a way to
  // find out whether anything has changed since; there is no real EPO
  // status endpoint to poll yet, so checkPatentReceiptStatus just
  // reports the status already stored on the application, but this at
  // least reloads it and surfaces the result instead of leaving the
  // page frozen at whatever it showed on load.
  async function handleCheckStatus() {
    if (!id || checkingStatus) return

    setCheckingStatus(true)
    setStatusMessage(null)
    setError(null)

    try {
      const result = await checkEuipoPaymentStatus({
        applicationType: "patent",
        applicationId: id
      })

      await loadApplication(id)

      setStatusMessage(
        result.status === "PAID"
          ? "The application has been filed with the EPO."
          : result.status === "ERROR"
          ? "The EPO could not process this application."
          : "No change yet. The application is still pending."
      )
    } catch (checkError) {
      setError(
        formatApiError(
          checkError,
          "The status could not be checked. Please reload the page and try again."
        )
      )
    } finally {
      setCheckingStatus(false)
    }
  }

  // Admin-only: confirms the bank transfer arrived and finalizes the
  // submission (PAYMENT_PENDING -> PAID), same action Design/Trademark
  // already expose through markCustomerPaymentReceived.
  async function handleMarkPaymentReceived() {
    if (!id || submitting) return

    setSubmitting(true)
    setError(null)
    setStatusMessage(null)

    try {
      await markCustomerPaymentReceived({
        applicationType: "patent",
        applicationId: id
      })
      await loadApplication(id)
      setStatusMessage("Payment marked as received. The application has been filed.")
    } catch (markError) {
      setError(
        formatApiError(
          markError,
          "The payment could not be marked as received. Please reload the page and try again."
        )
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleTestRealEpoImport() {
    if (!id || epoTestLoading) return

    setEpoTestLoading(true)
    setEpoTestError(null)
    setEpoTestResult(null)

    try {
      const result = await testRealEpoImport(id)
      setEpoTestResult(result)
    } catch (testError) {
      setEpoTestError(
        formatApiError(
          testError,
          "The real EPO import test failed. Please check the backend logs."
        )
      )
    } finally {
      setEpoTestLoading(false)
    }
  }

  if (loading) {
    return (
      <AppLayout title="Patent Payment">
        <div className="flex justify-center py-32 text-gray-600">
          <SpinnerText>Loading application...</SpinnerText>
        </div>
      </AppLayout>
    )
  }

  if (!application || !id) {
    return (
      <AppLayout title="Patent Payment">
        <div className="flex justify-center py-32 text-gray-600">
          Application not found.
        </div>
      </AppLayout>
    )
  }

  const documentTypes: string[] = (application.documents || []).map((doc: any) => doc.type)

  return (
    <AppLayout title="Patent Payment: Review application" wide>
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <PatentProgressBar
            complete={{
              applicant: !!application.applicants?.length,
              signature: !!application.signature?.signerName,
              payment: paymentIsPaid
            }}
          />
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10 pb-36">
        <h1 className="mb-6 text-xl font-semibold text-gray-800">
          Review your patent application
        </h1>

        {isAdmin && application.procedureType === "EP1001" && (
          <div className="mb-6 rounded-2xl border border-purple-200 bg-purple-50 p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-purple-900">
                Admin: Test real EPO import (demo environment)
              </h2>
              <button
                type="button"
                onClick={handleTestRealEpoImport}
                disabled={epoTestLoading}
                className="rounded bg-purple-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {epoTestLoading ? <SpinnerText>Sending...</SpinnerText> : "Send to EPO OLF2.0 demo"}
              </button>
            </div>
            <p className="mb-2 text-xs text-purple-700">
              Sends this application&apos;s real data to the EPO OLF2.0 demo API as a draft.
              This is not a legally valid filing and does not affect this application&apos;s status.
            </p>

            {epoTestError && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {epoTestError}
              </div>
            )}

            {epoTestResult && (
              <div className="space-y-2 rounded border border-purple-200 bg-white px-3 py-2 text-xs text-gray-700">
                <div>
                  <span className="font-medium">importStatus:</span>{" "}
                  {epoTestResult.importStatus || "(none)"}
                </div>

                {epoTestResult.submissionUrl && (
                  <div>
                    <a
                      href={epoTestResult.submissionUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-purple-700 underline"
                    >
                      Open draft on the EPO demo filing site
                    </a>
                  </div>
                )}

                {Array.isArray(epoTestResult.errorsList) && epoTestResult.errorsList.length > 0 && (
                  <div>
                    <span className="font-medium">Remaining issues:</span>
                    <ul className="ml-4 list-disc">
                      {epoTestResult.errorsList.map((item: any, index: number) => (
                        <li key={index}>
                          {item.message}
                          {item.description && (
                            <span className="block text-gray-600">{item.description}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(epoTestResult.warningList) && epoTestResult.warningList.length > 0 && (
                  <div>
                    <span className="font-medium">Warnings:</span>
                    <ul className="ml-4 list-disc text-gray-600">
                      {epoTestResult.warningList.map((item: any, index: number) => (
                        <li key={index}>{item.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {error && !showConfirm && (
          <div className="mb-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="divide-y divide-gray-200">
            {(paymentResult === "cancelled" ||
              applicationStatus === "PAYMENT_PENDING" ||
              applicationStatus === "AWAITING_RELEASE" ||
              applicationStatus === "PAID" ||
              applicationStatus === "ERROR") && (
              <div className="space-y-3 p-6 sm:p-8">
                {paymentResult === "cancelled" && (
                  <div className="rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                    Payment was cancelled. You can start the payment again when you are ready.
                  </div>
                )}

                {applicationStatus === "AWAITING_RELEASE" && (
                  <div className="rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    {isAdmin
                      ? "Waiting for release. The attorney reviews this application and files it afterwards."
                      : "Thank you. Your application is being reviewed by our attorney and will be filed afterwards. You will receive a confirmation with the invoice by e-mail, and the filing report once it has been filed."}
                  </div>
                )}

                {applicationStatus === "PAYMENT_PENDING" && (
                  <div className="space-y-3 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    <p>Payment is pending. The patent application has been submitted for bank transfer payment.</p>
                    {patentApplicationNumber && (
                      <p>Application number: {patentApplicationNumber}</p>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleCheckStatus}
                        disabled={checkingStatus}
                        className="rounded border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-800 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {checkingStatus ? <SpinnerText>Checking...</SpinnerText> : "Check status"}
                      </button>

                      {isAdmin && (
                        <button
                          type="button"
                          onClick={handleMarkPaymentReceived}
                          disabled={submitting}
                          className="rounded bg-blue-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Mark payment as received
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {applicationStatus === "PAID" && (
                  <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    Payment is completed. This patent application has already been submitted.
                    {patentFilingIdentifier && (
                      <span> {patentFilingIdentifier.label}: {patentFilingIdentifier.value}</span>
                    )}
                  </div>
                )}

                {applicationStatus === "ERROR" && (
                  <div className="space-y-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <p>Submission failed. An admin can review and submit the patent application again.</p>
                    <button
                      type="button"
                      onClick={handleCheckStatus}
                      disabled={checkingStatus}
                      className="rounded border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {checkingStatus ? <SpinnerText>Checking...</SpinnerText> : "Check status"}
                    </button>
                  </div>
                )}

                {statusMessage && (
                  <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    {statusMessage}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-x-12 gap-y-6 p-6 sm:grid-cols-2 sm:p-8">
              <Field label="Reference number">
                {application.referenceNumber?.referenceNumber || ""}
              </Field>

              <Field label="Title of the invention">
                {application.title || ""}
              </Field>

              <Field label="Language of filing">
                {application.languages?.filingLanguage || ""}
              </Field>

              <Field label="Representative">
                <Bullets
                  items={[
                    application.representative?.name,
                    application.representative?.street,
                    `${application.representative?.city || ""}, ${application.representative?.country || ""}`
                  ]}
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-x-12 gap-y-6 p-6 sm:grid-cols-2 sm:p-8">
              <Field label="Applicants">
                {application.applicants?.length ? (
                  application.applicants.map((a: any, index: number) => (
                    <div key={a._id || index}>
                      {a.type === "natural"
                        ? `${a.firstName || ""} ${a.lastName || ""}`.trim()
                        : a.legalName || ""}
                    </div>
                  ))
                ) : (
                  <div></div>
                )}
              </Field>

              <Field label="Inventors">
                {application.inventors?.length ? (
                  application.inventors.map((inventor: any, index: number) => {
                    const name =
                      `${inventor.firstName || ""} ${inventor.lastName || ""}`.trim() ||
                      inventor.name
                    const right = inventor.applicantIsInventor
                      ? "applicant is inventor"
                      : ORIGIN_OF_RIGHT_LABELS[inventor.rightAcquisition] ||
                        inventor.rightAcquisition
                    return (
                      <div key={index}>
                        {name}
                        {inventor.waiverIndicator ? " (waived)" : ""}
                        {right ? ` · ${right}` : ""}
                      </div>
                    )
                  })
                ) : (
                  <div></div>
                )}
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-x-12 gap-y-6 p-6 sm:grid-cols-2 sm:p-8">
              <Field label="Priority claims">
                {application.priorities?.length ? (
                  application.priorities.map((priority: any, index: number) => (
                    <div key={priority._id || index}>
                      {priority.office} · {priority.filingNumber}
                    </div>
                  ))
                ) : (
                  <div></div>
                )}
              </Field>
            </div>

            <div className="p-6 sm:p-8">
              <div className="mb-4 text-sm font-semibold text-gray-900">
                Documents
              </div>

              {documentTypes.length ? (
                <Bullets
                  items={documentTypes.map(
                    (type) => DOCUMENT_TYPE_LABELS[type] || type
                  )}
                />
              ) : (
                <div className="text-sm text-gray-500"></div>
              )}

              {(application.claimsFiledLater || application.translationFiledLater) && (
                <ul className="mt-3 list-disc pl-5 text-sm text-gray-500">
                  {application.claimsFiledLater && <li>Claims will be filed later</li>}
                  {application.translationFiledLater && (
                    <li>A translation of the application will be filed later</li>
                  )}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-1 gap-x-12 gap-y-6 p-6 sm:grid-cols-2 sm:p-8">
              <Field label="Signature">
                {application.signature?.signerName || ""}
              </Field>
            </div>

            <div className="p-6 sm:p-8">
              <ServiceDescriptions
                type={application.procedureType === "EP1200" ? "patent-pct" : "patent-direct"}
              />
              <PatentFeesSection
                applicationId={id}
                disabled={userPaymentIsLocked && !isAdmin}
                title="Total Fees"
                fixHref={`/patent-applications/${id}/${application.procedureType === "EP1200" ? "ep1200/" : ""}edit`}
              />
            </div>

            <div className="p-6 sm:p-8">
              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-blue-800"
                />
                I confirm that all information provided above is correct and complete.
              </label>
            </div>

            <div className="p-6 sm:p-8">
              <div
                className={`space-y-4 rounded-xl border border-gray-300 p-6 ${
                  !confirmed || (userPaymentIsLocked && !isAdmin)
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white"
                }`}
              >
                <div className="text-sm font-medium text-gray-800">
                  Payment
                </div>

                <p className="text-sm">
                  {userPaymentIsLocked && !isAdmin
                    ? "No further payment action is required for this patent application."
                    : "You can pay the full filing amount now by Stripe or receive an invoice for bank transfer."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-between gap-4 border-t bg-gray-100 px-6 py-3 shadow-sm">
        <p className="text-sm text-gray-700">
          Review your application and fees before continuing to payment.
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => navigate("/applications")}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!paymentReady}
            onClick={() => setShowConfirm(true)}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition ${
              paymentReady
                ? "bg-dantzOrange hover:brightness-110 active:scale-[0.98]"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Continue
          </button>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-6">
            <h3 className="text-lg font-semibold">Choose payment method</h3>

            <p className="text-sm text-gray-700">
              Pay now with Stripe, or receive an invoice for a bank transfer. In both cases our attorney reviews your application and files it afterwards.
            </p>

            {error && (
              <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
                className="rounded border px-4 py-2 text-sm"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleInvoiceSubmit}
                disabled={submitting}
                className="rounded border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
              >
                Send invoice
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className={`rounded px-4 py-2 text-sm font-medium text-white ${
                  submitting
                    ? "cursor-not-allowed bg-orange-300"
                    : "bg-orange-500 hover:bg-orange-600"
                }`}
              >
                {submitting ? <SpinnerText>Processing...</SpinnerText> : "Pay with Stripe"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvoiceSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Invoice request received
            </h3>

            <p className="text-sm text-gray-700">
              Your patent application was received. It will be reviewed by our attorney and filed afterwards. You will receive a confirmation with our invoice by e-mail. The status will remain payment pending until the payment is received and confirmed.
            </p>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowInvoiceSuccess(false)
                  navigate("/home")
                }}
                className="rounded bg-orange-500 px-4 py-2 text-sm text-white hover:bg-orange-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Payment received
            </h3>

            <p className="text-sm text-gray-700">
              Your payment was received. Your patent application will be reviewed by our attorney and filed afterwards. You will receive a confirmation with the invoice by e-mail, and the filing report once it has been filed.
            </p>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowPaymentSuccess(false)
                  navigate("/home")
                }}
                className="rounded bg-orange-500 px-4 py-2 text-sm text-white hover:bg-orange-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

function Field({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-1.5 text-sm font-semibold text-gray-900">{label}</div>
      <div className="space-y-1 text-sm text-gray-600">{children}</div>
    </div>
  )
}

function Bullets({
  items
}: {
  items: (string | false | null | undefined)[]
}) {
  const visible = items.filter(Boolean) as string[]

  if (visible.length === 0) {
    return <div></div>
  }

  return (
    <ul className="space-y-1">
      {visible.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gray-400" />
          {item}
        </li>
      ))}
    </ul>
  )
}
